import type { EmojiCategory, IEmoji, IEmojiCategoryMeta } from '../types';

/**
 * Emoji categories with display metadata
 */
export const EMOJI_CATEGORIES: IEmojiCategoryMeta[] = [
  { id: 'smileys', icon: '😀', i18nKey: 'markdown_editor.emoji.categories.smileys' },
  { id: 'people', icon: '👋', i18nKey: 'markdown_editor.emoji.categories.people' },
  { id: 'animals', icon: '🐶', i18nKey: 'markdown_editor.emoji.categories.animals' },
  { id: 'food', icon: '🍔', i18nKey: 'markdown_editor.emoji.categories.food' },
  { id: 'activities', icon: '⚽', i18nKey: 'markdown_editor.emoji.categories.activities' },
  { id: 'travel', icon: '✈️', i18nKey: 'markdown_editor.emoji.categories.travel' },
  { id: 'objects', icon: '💡', i18nKey: 'markdown_editor.emoji.categories.objects' },
  { id: 'symbols', icon: '🔣', i18nKey: 'markdown_editor.emoji.categories.symbols' },
  { id: 'flags', icon: '🚩', i18nKey: 'markdown_editor.emoji.categories.flags' },
];

/**
 * Comprehensive emoji data with search keywords
 */
export const EMOJI_DATA: IEmoji[] = [
  // Smileys & Emotion
  { char: '😀', name: 'grinning face', keywords: ['happy', 'smile', 'joy'], category: 'smileys' },
  {
    char: '😃',
    name: 'grinning face with big eyes',
    keywords: ['happy', 'joy', 'smile'],
    category: 'smileys',
  },
  {
    char: '😄',
    name: 'grinning face with smiling eyes',
    keywords: ['happy', 'joy', 'smile'],
    category: 'smileys',
  },
  {
    char: '😁',
    name: 'beaming face with smiling eyes',
    keywords: ['happy', 'joy', 'smile'],
    category: 'smileys',
  },
  {
    char: '😆',
    name: 'grinning squinting face',
    keywords: ['happy', 'laugh', 'smile'],
    category: 'smileys',
  },
  {
    char: '😅',
    name: 'grinning face with sweat',
    keywords: ['sweat', 'smile', 'relief'],
    category: 'smileys',
  },
  {
    char: '🤣',
    name: 'rolling on the floor laughing',
    keywords: ['laugh', 'funny', 'lol'],
    category: 'smileys',
  },
  {
    char: '😂',
    name: 'face with tears of joy',
    keywords: ['laugh', 'funny', 'joy'],
    category: 'smileys',
  },
  {
    char: '🙂',
    name: 'slightly smiling face',
    keywords: ['smile', 'happy', 'content'],
    category: 'smileys',
  },
  {
    char: '🙃',
    name: 'upside-down face',
    keywords: ['silly', 'funny', 'playful'],
    category: 'smileys',
  },
  { char: '😉', name: 'winking face', keywords: ['wink', 'flirt', 'playful'], category: 'smileys' },
  {
    char: '😊',
    name: 'smiling face with smiling eyes',
    keywords: ['happy', 'blush', 'smile'],
    category: 'smileys',
  },
  {
    char: '😇',
    name: 'smiling face with halo',
    keywords: ['angel', 'innocent', 'good'],
    category: 'smileys',
  },
  {
    char: '🥰',
    name: 'smiling face with hearts',
    keywords: ['love', 'in love', 'happy'],
    category: 'smileys',
  },
  { char: '😍', name: 'heart eyes', keywords: ['love', 'adore', 'beautiful'], category: 'smileys' },
  {
    char: '🤩',
    name: 'star-struck',
    keywords: ['amazing', 'wow', 'impressed'],
    category: 'smileys',
  },
  {
    char: '😘',
    name: 'face blowing a kiss',
    keywords: ['kiss', 'love', 'affection'],
    category: 'smileys',
  },
  { char: '😗', name: 'kissing face', keywords: ['kiss', 'affection'], category: 'smileys' },
  {
    char: '😚',
    name: 'kissing face with closed eyes',
    keywords: ['kiss', 'love'],
    category: 'smileys',
  },
  {
    char: '😙',
    name: 'kissing face with smiling eyes',
    keywords: ['kiss', 'happy', 'love'],
    category: 'smileys',
  },

  // People & Body
  { char: '👋', name: 'waving hand', keywords: ['hello', 'goodbye', 'wave'], category: 'people' },
  {
    char: '🤝',
    name: 'handshake',
    keywords: ['agreement', 'deal', 'partnership'],
    category: 'people',
  },
  { char: '👍', name: 'thumbs up', keywords: ['good', 'ok', 'yes', 'like'], category: 'people' },
  { char: '👎', name: 'thumbs down', keywords: ['bad', 'no', 'dislike'], category: 'people' },
  { char: '👌', name: 'OK hand', keywords: ['ok', 'perfect', 'yes'], category: 'people' },
  { char: '✌️', name: 'victory hand', keywords: ['peace', 'victory', 'v'], category: 'people' },
  {
    char: '🤞',
    name: 'crossed fingers',
    keywords: ['luck', 'hope', 'fingers crossed'],
    category: 'people',
  },
  { char: '🤟', name: 'love-you gesture', keywords: ['love', 'i love you'], category: 'people' },
  {
    char: '🤘',
    name: 'sign of the horns',
    keywords: ['rock', 'metal', 'devil horns'],
    category: 'people',
  },
  { char: '🤙', name: 'call me hand', keywords: ['call me', 'phone'], category: 'people' },
  {
    char: '👈',
    name: 'backhand index pointing left',
    keywords: ['left', 'point'],
    category: 'people',
  },
  {
    char: '👉',
    name: 'backhand index pointing right',
    keywords: ['right', 'point'],
    category: 'people',
  },
  { char: '👆', name: 'backhand index pointing up', keywords: ['up', 'point'], category: 'people' },
  {
    char: '👇',
    name: 'backhand index pointing down',
    keywords: ['down', 'point'],
    category: 'people',
  },
  { char: '☝️', name: 'index pointing up', keywords: ['up', 'point', 'one'], category: 'people' },
  {
    char: '🙏',
    name: 'folded hands',
    keywords: ['please', 'thank you', 'pray'],
    category: 'people',
  },

  // Animals & Nature
  { char: '🐶', name: 'dog face', keywords: ['dog', 'pet', 'animal'], category: 'animals' },
  { char: '🐱', name: 'cat face', keywords: ['cat', 'pet', 'animal'], category: 'animals' },
  { char: '🐭', name: 'mouse face', keywords: ['mouse', 'rodent', 'animal'], category: 'animals' },
  { char: '🐹', name: 'hamster face', keywords: ['hamster', 'pet', 'rodent'], category: 'animals' },
  { char: '🐰', name: 'rabbit face', keywords: ['rabbit', 'bunny', 'pet'], category: 'animals' },
  { char: '🦊', name: 'fox face', keywords: ['fox', 'animal', 'clever'], category: 'animals' },
  { char: '🐻', name: 'bear face', keywords: ['bear', 'animal', 'strong'], category: 'animals' },
  { char: '🐼', name: 'panda face', keywords: ['panda', 'bear', 'cute'], category: 'animals' },
  { char: '🐨', name: 'koala face', keywords: ['koala', 'animal', 'cute'], category: 'animals' },
  { char: '🐯', name: 'tiger face', keywords: ['tiger', 'animal', 'wild'], category: 'animals' },
  { char: '🦁', name: 'lion face', keywords: ['lion', 'animal', 'king'], category: 'animals' },
  { char: '🐮', name: 'cow face', keywords: ['cow', 'animal', 'farm'], category: 'animals' },
  { char: '🐷', name: 'pig face', keywords: ['pig', 'animal', 'farm'], category: 'animals' },
  { char: '🐸', name: 'frog face', keywords: ['frog', 'animal', 'green'], category: 'animals' },
  {
    char: '🐵',
    name: 'monkey face',
    keywords: ['monkey', 'animal', 'banana'],
    category: 'animals',
  },
  {
    char: '🦄',
    name: 'unicorn face',
    keywords: ['unicorn', 'magic', 'fantasy'],
    category: 'animals',
  },

  // Food & Drink
  { char: '🍎', name: 'red apple', keywords: ['apple', 'fruit', 'red'], category: 'food' },
  { char: '🍊', name: 'orange', keywords: ['orange', 'fruit', 'citrus'], category: 'food' },
  { char: '🍋', name: 'lemon', keywords: ['lemon', 'fruit', 'citrus', 'sour'], category: 'food' },
  { char: '🍌', name: 'banana', keywords: ['banana', 'fruit', 'yellow'], category: 'food' },
  { char: '🍉', name: 'watermelon', keywords: ['watermelon', 'fruit', 'summer'], category: 'food' },
  { char: '🍇', name: 'grapes', keywords: ['grapes', 'fruit', 'wine'], category: 'food' },
  { char: '🍓', name: 'strawberry', keywords: ['strawberry', 'fruit', 'red'], category: 'food' },
  { char: '🍒', name: 'cherries', keywords: ['cherries', 'fruit', 'red'], category: 'food' },
  { char: '🍑', name: 'peach', keywords: ['peach', 'fruit', 'pink'], category: 'food' },
  { char: '🥝', name: 'kiwi fruit', keywords: ['kiwi', 'fruit', 'green'], category: 'food' },
  { char: '🍅', name: 'tomato', keywords: ['tomato', 'vegetable', 'red'], category: 'food' },
  { char: '🥑', name: 'avocado', keywords: ['avocado', 'fruit', 'green'], category: 'food' },
  { char: '🍔', name: 'hamburger', keywords: ['burger', 'fast food', 'meat'], category: 'food' },
  { char: '🍕', name: 'pizza', keywords: ['pizza', 'italian', 'cheese'], category: 'food' },
  { char: '🌮', name: 'taco', keywords: ['taco', 'mexican', 'food'], category: 'food' },
  { char: '🍿', name: 'popcorn', keywords: ['popcorn', 'snack', 'movie'], category: 'food' },
  {
    char: '🥤',
    name: 'cup with straw',
    keywords: ['drink', 'straw', 'beverage'],
    category: 'food',
  },
  { char: '☕', name: 'hot beverage', keywords: ['coffee', 'tea', 'hot'], category: 'food' },
  { char: '🍺', name: 'beer mug', keywords: ['beer', 'alcohol', 'drink'], category: 'food' },
  { char: '🍷', name: 'wine glass', keywords: ['wine', 'alcohol', 'drink'], category: 'food' },

  // Activities
  {
    char: '⚽',
    name: 'soccer ball',
    keywords: ['soccer', 'football', 'sport'],
    category: 'activities',
  },
  {
    char: '🏀',
    name: 'basketball',
    keywords: ['basketball', 'sport', 'hoop'],
    category: 'activities',
  },
  {
    char: '🏈',
    name: 'american football',
    keywords: ['football', 'sport', 'nfl'],
    category: 'activities',
  },
  { char: '⚾', name: 'baseball', keywords: ['baseball', 'sport', 'mlb'], category: 'activities' },
  { char: '🥎', name: 'softball', keywords: ['softball', 'sport'], category: 'activities' },
  { char: '🎾', name: 'tennis', keywords: ['tennis', 'sport', 'racket'], category: 'activities' },
  { char: '🏐', name: 'volleyball', keywords: ['volleyball', 'sport'], category: 'activities' },
  { char: '🏉', name: 'rugby football', keywords: ['rugby', 'sport'], category: 'activities' },
  {
    char: '🎱',
    name: 'pool 8 ball',
    keywords: ['pool', 'billiards', '8 ball'],
    category: 'activities',
  },
  {
    char: '🏓',
    name: 'ping pong',
    keywords: ['ping pong', 'table tennis'],
    category: 'activities',
  },
  {
    char: '🎯',
    name: 'direct hit',
    keywords: ['target', 'bullseye', 'dart'],
    category: 'activities',
  },
  { char: '🎲', name: 'game die', keywords: ['dice', 'game', 'gambling'], category: 'activities' },
  {
    char: '🎮',
    name: 'video game',
    keywords: ['gaming', 'game', 'controller'],
    category: 'activities',
  },
  {
    char: '🎰',
    name: 'slot machine',
    keywords: ['casino', 'gambling', 'slots'],
    category: 'activities',
  },
  {
    char: '🎵',
    name: 'musical note',
    keywords: ['music', 'note', 'sound'],
    category: 'activities',
  },
  {
    char: '🎶',
    name: 'musical notes',
    keywords: ['music', 'notes', 'song'],
    category: 'activities',
  },

  // Travel & Places
  { char: '🚗', name: 'automobile', keywords: ['car', 'vehicle', 'drive'], category: 'travel' },
  { char: '🚕', name: 'taxi', keywords: ['taxi', 'cab', 'transport'], category: 'travel' },
  {
    char: '🚙',
    name: 'sport utility vehicle',
    keywords: ['suv', 'car', 'vehicle'],
    category: 'travel',
  },
  { char: '🚌', name: 'bus', keywords: ['bus', 'transport', 'public'], category: 'travel' },
  { char: '🚎', name: 'trolleybus', keywords: ['trolley', 'bus', 'transport'], category: 'travel' },
  { char: '🏎️', name: 'racing car', keywords: ['race', 'car', 'fast'], category: 'travel' },
  { char: '🚓', name: 'police car', keywords: ['police', 'car', 'emergency'], category: 'travel' },
  {
    char: '🚑',
    name: 'ambulance',
    keywords: ['ambulance', 'emergency', 'medical'],
    category: 'travel',
  },
  { char: '🚒', name: 'fire engine', keywords: ['fire', 'truck', 'emergency'], category: 'travel' },
  { char: '🚐', name: 'minibus', keywords: ['van', 'bus', 'transport'], category: 'travel' },
  { char: '🚚', name: 'truck', keywords: ['truck', 'delivery', 'transport'], category: 'travel' },
  {
    char: '🚛',
    name: 'tractor-trailer',
    keywords: ['truck', 'trailer', 'transport'],
    category: 'travel',
  },
  { char: '✈️', name: 'airplane', keywords: ['plane', 'fly', 'travel'], category: 'travel' },
  {
    char: '🚁',
    name: 'helicopter',
    keywords: ['helicopter', 'fly', 'transport'],
    category: 'travel',
  },
  {
    char: '🚂',
    name: 'steam locomotive',
    keywords: ['train', 'locomotive', 'transport'],
    category: 'travel',
  },
  {
    char: '🚃',
    name: 'railway car',
    keywords: ['train', 'railway', 'transport'],
    category: 'travel',
  },

  // Objects
  { char: '⌚', name: 'watch', keywords: ['time', 'clock', 'watch'], category: 'objects' },
  {
    char: '📱',
    name: 'mobile phone',
    keywords: ['phone', 'mobile', 'smartphone'],
    category: 'objects',
  },
  {
    char: '📲',
    name: 'mobile phone with arrow',
    keywords: ['phone', 'download', 'mobile'],
    category: 'objects',
  },
  {
    char: '💻',
    name: 'laptop computer',
    keywords: ['laptop', 'computer', 'work'],
    category: 'objects',
  },
  {
    char: '⌨️',
    name: 'keyboard',
    keywords: ['keyboard', 'typing', 'computer'],
    category: 'objects',
  },
  {
    char: '🖥️',
    name: 'desktop computer',
    keywords: ['computer', 'desktop', 'monitor'],
    category: 'objects',
  },
  { char: '🖨️', name: 'printer', keywords: ['printer', 'print', 'office'], category: 'objects' },
  {
    char: '🖱️',
    name: 'computer mouse',
    keywords: ['mouse', 'computer', 'click'],
    category: 'objects',
  },
  {
    char: '🖲️',
    name: 'trackball',
    keywords: ['trackball', 'mouse', 'computer'],
    category: 'objects',
  },
  { char: '🕹️', name: 'joystick', keywords: ['gaming', 'joystick', 'game'], category: 'objects' },
  {
    char: '💽',
    name: 'computer disk',
    keywords: ['disk', 'storage', 'computer'],
    category: 'objects',
  },
  { char: '💾', name: 'floppy disk', keywords: ['disk', 'save', 'storage'], category: 'objects' },
  { char: '💿', name: 'optical disk', keywords: ['cd', 'disk', 'storage'], category: 'objects' },
  { char: '📀', name: 'dvd', keywords: ['dvd', 'disk', 'movie'], category: 'objects' },
  { char: '📷', name: 'camera', keywords: ['camera', 'photo', 'picture'], category: 'objects' },
  {
    char: '📸',
    name: 'camera with flash',
    keywords: ['camera', 'flash', 'photo'],
    category: 'objects',
  },

  // Symbols
  { char: '❤️', name: 'red heart', keywords: ['love', 'heart', 'red'], category: 'symbols' },
  { char: '🧡', name: 'orange heart', keywords: ['love', 'heart', 'orange'], category: 'symbols' },
  { char: '💛', name: 'yellow heart', keywords: ['love', 'heart', 'yellow'], category: 'symbols' },
  { char: '💚', name: 'green heart', keywords: ['love', 'heart', 'green'], category: 'symbols' },
  { char: '💙', name: 'blue heart', keywords: ['love', 'heart', 'blue'], category: 'symbols' },
  { char: '💜', name: 'purple heart', keywords: ['love', 'heart', 'purple'], category: 'symbols' },
  { char: '🖤', name: 'black heart', keywords: ['love', 'heart', 'black'], category: 'symbols' },
  { char: '🤍', name: 'white heart', keywords: ['love', 'heart', 'white'], category: 'symbols' },
  { char: '🤎', name: 'brown heart', keywords: ['love', 'heart', 'brown'], category: 'symbols' },
  {
    char: '💔',
    name: 'broken heart',
    keywords: ['heartbreak', 'sad', 'broken'],
    category: 'symbols',
  },
  {
    char: '❣️',
    name: 'exclamation heart',
    keywords: ['love', 'heart', 'excitement'],
    category: 'symbols',
  },
  { char: '💕', name: 'two hearts', keywords: ['love', 'hearts', 'couple'], category: 'symbols' },
  {
    char: '💞',
    name: 'revolving hearts',
    keywords: ['love', 'hearts', 'romance'],
    category: 'symbols',
  },
  {
    char: '💓',
    name: 'beating heart',
    keywords: ['love', 'heart', 'heartbeat'],
    category: 'symbols',
  },
  {
    char: '💗',
    name: 'growing heart',
    keywords: ['love', 'heart', 'growing'],
    category: 'symbols',
  },
  {
    char: '💖',
    name: 'sparkling heart',
    keywords: ['love', 'heart', 'sparkle'],
    category: 'symbols',
  },
  { char: '💘', name: 'cupid', keywords: ['love', 'heart', 'arrow'], category: 'symbols' },
  {
    char: '💝',
    name: 'heart with ribbon',
    keywords: ['love', 'heart', 'gift'],
    category: 'symbols',
  },

  // Flags
  { char: '🏳️', name: 'white flag', keywords: ['flag', 'white', 'surrender'], category: 'flags' },
  { char: '🏴', name: 'black flag', keywords: ['flag', 'black', 'pirate'], category: 'flags' },
  { char: '🏴‍☠️', name: 'pirate flag', keywords: ['pirate', 'flag', 'skull'], category: 'flags' },
  {
    char: '🚩',
    name: 'triangular flag',
    keywords: ['flag', 'triangle', 'marker'],
    category: 'flags',
  },
  {
    char: '🚧',
    name: 'construction',
    keywords: ['construction', 'warning', 'work'],
    category: 'flags',
  },
  {
    char: '🚨',
    name: 'police car light',
    keywords: ['police', 'emergency', 'siren'],
    category: 'flags',
  },
  {
    char: '🚦',
    name: 'vertical traffic light',
    keywords: ['traffic', 'light', 'signal'],
    category: 'flags',
  },
  {
    char: '🚥',
    name: 'horizontal traffic light',
    keywords: ['traffic', 'light', 'signal'],
    category: 'flags',
  },
  { char: '🚏', name: 'bus stop', keywords: ['bus', 'stop', 'transport'], category: 'flags' },
  { char: '🛑', name: 'stop sign', keywords: ['stop', 'sign', 'octagon'], category: 'flags' },
  { char: '⛔', name: 'no entry', keywords: ['no', 'entry', 'forbidden'], category: 'flags' },
  { char: '📛', name: 'name badge', keywords: ['badge', 'name', 'id'], category: 'flags' },
  { char: '🆘', name: 'sos button', keywords: ['sos', 'help', 'emergency'], category: 'flags' },
  { char: '🆗', name: 'ok button', keywords: ['ok', 'button', 'good'], category: 'flags' },
  { char: '🆙', name: 'up button', keywords: ['up', 'button', 'arrow'], category: 'flags' },
  { char: '🆒', name: 'cool button', keywords: ['cool', 'button', 'awesome'], category: 'flags' },
];

/**
 * Map of emojis grouped by category for efficient lookup
 */
export const EMOJI_BY_CATEGORY: Map<EmojiCategory, IEmoji[]> = new Map(
  EMOJI_CATEGORIES.map((cat) => [cat.id, EMOJI_DATA.filter((e) => e.category === cat.id)]),
);
